import chroma from 'chroma-js';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import Popover from '@ui/popover/popover';
import ColorPicker from '@ui/color-picker/color-picker';
import type { OnColorPickerChangeResult } from '@ui/color-picker/color-picker.types';
import { ICONS } from '@ui/icons/types';
import classes from './color-pill.module.css';

interface ColorPillProps {
  label: string;
  /** Allow undefined so background colors don't have to be set */
  value: string | undefined;
  onChange: (value: string) => void;
  isOverridden?: boolean;
  onReset?: () => void;
  disabled?: boolean;
}

function toDisplayHex(value: string | undefined): string {
  if (!value || !chroma.valid(value)) return '#000000';
  const [r, g, b] = chroma(value).rgb();
  return chroma.rgb(r, g, b).hex();
}

export default function ColorPill({ label, value, onChange, isOverridden, onReset, disabled }: ColorPillProps) {
  // resetting some colors (border color) sets the resolved color to black with no alpha #00000000 - treat this as unset
  const isUnset = value === undefined || value === '#00000000';
  const hex = toDisplayHex(value);

  const handlePickerChange = (result: OnColorPickerChangeResult) => {
    onChange(result.opacity >= 1 ? result.hex : result.rgba);
  };

  return (
    <Container className={'flex-column gap-2 flex-1 ' + classes.container} minWidth="0" opacity={disabled ? 0.5 : 1}>
      <Text tag="label" design="GlobalIcoText3" className="L2 padding-l-16">
        {label}
      </Text>
      <Container
        className="flex ai-center gap-8 padding-4 radius-100 no-select jc-space-between"
        border="1px solid var(--global-ico-text-3)"
        hoverBackground={disabled ? undefined : 'var(--gray-800)'}
        transitionDuration="0.2s"
        height="32px"
        width="100%"
      >
        <Popover
          disabled={disabled}
          width="fit-content"
          contentClassName="position-fixed"
          renderTriggerButton={() => (
            <Container className={`flex ai-center gap-8 flex-1 ${disabled ? '' : 'cursor-pointer'}`} minWidth="0">
              <Container
                className="square-24 radius-full"
                style={
                  isUnset
                    ? {
                        backgroundImage:
                          'conic-gradient(var(--global-cont-3) 0deg 90deg, transparent 90deg 180deg, var(--global-cont-3) 180deg 270deg, transparent 270deg 360deg)',
                        backgroundSize: '8px 8px',
                        border: '1px solid var(--global-ico-text-3)',
                      }
                    : { backgroundColor: value }
                }
              />
              {isUnset ? (
                <Text color="var(--gray-500)" className="P2">
                  None
                </Text>
              ) : (
                <div className="flex ai-center">
                  <Text color="var(--gray-500)" className="P2">
                    #&nbsp;
                  </Text>
                  <Text color="var(--gray-100)" className="P2 text-uppercase">
                    {hex.replace('#', '')}
                  </Text>
                </div>
              )}
            </Container>
          )}
        >
          <ColorPicker value={isUnset ? undefined : value} onChange={handlePickerChange} />
        </Popover>

        {!disabled && isOverridden && onReset && (
          <Button
            clear
            className={'ai-center jc-center flex-shrink-0 position-relative ' + classes.resetButton}
            width="20px"
            height="20px"
            padding="0 6px 0 0"
            backgroundColor="transparent"
            hoverChildrenColor="var(--global-ico-text-1)"
            onClick={(e) => {
              e.stopPropagation();
              onReset();
            }}
            aria-label={`Reset ${label} to default`}
            title="Reset to default"
          >
            <Icon name={ICONS.UNDO} size={11} viewBox="-1 -2 20 20" />
          </Button>
        )}
      </Container>
    </Container>
  );
}
